'use client';

import React from 'react';

import { LINKS } from '@/constants';
import NextLink from 'next/link';
import { logTrackingEvent } from '@/utils/tracking';
import Icon, { type IconName } from '@/components/icons';
import { type LinkSet } from '@/constants/links';

import { ANCAPLogo, RedbookLogo, TACLogo, UCSRCombinedLogo, VicLogo } from './logos';
import { LogoLink } from './logo-link';

type FooterPageLink = {
  title: string;
  url: string;

  openInNewWindow?: boolean;
};

type FooterSocialLink = {
  iconName: IconName;
  url: string;
};

type FooterProps = {
  termsAndPrivacyLinks?: FooterPageLink[] | LinkSet;
  socialLinks?: FooterSocialLink[];
  pageLinks?: FooterPageLink[];
};

const baseNavigationEvent = {
  event: 'navigation_click',
  navigation_type: 'footer_nav',
  nav_level: '1',
};

const headingClasses =
  'text-[12px] min-[576px]:text-[13px] min-[992px]:text-[16px] text-greyscale-0 flex justify-center leading-[22px] mb-4 mt-8 min-h-[22px] min-[576px]:text-left min-[576px]:justify-start';

const Footer = ({ termsAndPrivacyLinks = [], socialLinks = [], pageLinks = [] }: FooterProps) => {
  return (
    <footer className="w-full mt-auto">
      <div className="bg-greyscale-1000 py-4">
        <div className="container-default grid grid-cols-1 min-[576px]:grid-cols-2 gap-x-8 gap-y-2">
          <div>
            <p role="heading" aria-level={2} className={headingClasses}>
              Join the conversation
            </p>
            <ul className="flex gap-4 p-0 list-none justify-center min-[576px]:justify-start">
              {socialLinks.map((link, index) => (
                <li key={index}>
                  <NextLink
                    className="focus:outline-2 focus:outline-primary-400 hover:outline-2 hover:outline-primary-400"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      logTrackingEvent({
                        ...baseNavigationEvent,
                        click_url: link.url,
                        click_text: link.iconName,
                      })
                    }
                  >
                    <Icon
                      name={link.iconName}
                      className="fill-greyscale-25 h-[18px] w-[18px] min-[768px]:h-[14px] min-[768px]:w-[14px] min-[992px]:h-[22px] min-[992px]:w-[22px]"
                    />
                  </NextLink>
                </li>
              ))}
            </ul>
            <ul className="flex flex-wrap mx-auto my-6 w-[208px] justify-center p-0 list-none min-[576px]:w-auto min-[576px]:justify-start min-[992px]:flex-nowrap min-[992px]:w-auto">
              <LogoLink href={LINKS.TAC} trackingText="TAC" size="default">
                <TACLogo />
              </LogoLink>
              <LogoLink href={LINKS.VICTORIA_GOVERNMENT} trackingText="Vic Gov" size="shortened">
                <VicLogo />
              </LogoLink>
              <LogoLink href={LINKS.ANCAP} trackingText="ANCAP" size="default">
                <ANCAPLogo />
              </LogoLink>
              <LogoLink size="default">
                <UCSRCombinedLogo />
              </LogoLink>
              <LogoLink size="shortened">
                <RedbookLogo />
              </LogoLink>
            </ul>
          </div>

          <div className="hidden min-[576px]:block">
            <p role="heading" aria-level={2} className={headingClasses}>
              More about
            </p>
            <ul className="grid grid-cols-2 gap-x-6 p-0 list-none">
              {pageLinks.map((link, index) => (
                <li
                  key={index}
                  className="font-[family-name:var(--font-circular-pro)] font-bold text-[18px] min-[992px]:text-[20px] mb-3 text-greyscale-0 no-underline hover:cursor-pointer hover:underline"
                >
                  <NextLink
                    className="focus:outline-2 focus:outline-primary-400 hover:outline-2 hover:outline-primary-400"
                    href={link.url}
                    onClick={() =>
                      logTrackingEvent({
                        ...baseNavigationEvent,
                        click_text: link.title,
                        click_url: link.url,
                      })
                    }
                  >
                    {link.title}
                  </NextLink>
                </li>
              ))}
            </ul>
          </div>

          <ul className="flex col-span-full justify-center p-0 pb-6 list-none min-[576px]:justify-start">
            {termsAndPrivacyLinks.map((link, index) => (
              <li key={index} className="inline-block">
                <span className="text-[11px] min-[576px]:text-[12px] min-[992px]:text-[13px] text-greyscale-0 no-underline hover:cursor-pointer hover:underline text-center min-[576px]:text-left">
                  <NextLink
                    href={link.url}
                    onClick={() =>
                      logTrackingEvent({
                        ...baseNavigationEvent,
                        click_text: link.title,
                        click_url: link.url,
                      })
                    }
                  >
                    {link.title}
                  </NextLink>
                  {index + 1 < termsAndPrivacyLinks.length ? <span className="px-1.5"> | </span> : null}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
